import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, X, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';

const OZ_TO_GRAM = 31.1035;
const REFRESH_INTERVAL = 60000; // 1 minute
const FUNCTIONS_BASE_URL = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || 'https://us-central1-chorost-store-center.cloudfunctions.net';

interface SpotPrice {
  price: number;
  change: number;
  formatted: string;
}

interface LivePrices {
  gold: SpotPrice | null;
  silver: SpotPrice | null;
  platinum: SpotPrice | null;
  palladium: SpotPrice | null;
  timestamp: string;
  source: string;
}

interface MetalPricesBarProps {
  className?: string;
  leftOffset?: number;
  isVisible: boolean;
  onVisibilityChange: (visible: boolean) => void;
  darkMode?: boolean;
}

const MetalPricesBar: React.FC<MetalPricesBarProps> = ({ className, leftOffset = 0, isVisible, onVisibilityChange, darkMode }) => {
  const [prices, setPrices] = useState<LivePrices | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  const fetchLivePrices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${FUNCTIONS_BASE_URL}/getLiveSpotPrices`);
      const data = await response.json();
      
      if (data.success) {
        setPrices(data);
        setLastFetch(new Date());
      } else {
        console.error('Error fetching spot prices:', data.error);
      }
    } catch (error) {
      console.error('Error fetching spot prices:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and set up auto-refresh
  useEffect(() => {
    fetchLivePrices();
    
    const interval = setInterval(fetchLivePrices, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchLivePrices]);

  // Save visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('metalPricesBarVisible', String(isVisible));
  }, [isVisible]);

  const toggleVisibility = (visible: boolean) => {
    onVisibilityChange(visible);
  };

  const formatPrice = (price: number, decimals: number = 2) => {
    return price.toLocaleString('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const metals = prices ? [
    { name: 'Gold', symbol: 'Au', data: prices.gold, color: 'text-amber-700', bg: 'bg-amber-100 border border-amber-200' },
    { name: 'Silver', symbol: 'Ag', data: prices.silver, color: 'text-slate-700', bg: 'bg-slate-200 border border-slate-300' },
    { name: 'Platinum', symbol: 'Pt', data: prices.platinum, color: 'text-cyan-800', bg: 'bg-cyan-100 border border-cyan-200' },
    { name: 'Palladium', symbol: 'Pd', data: prices.palladium, color: 'text-violet-800', bg: 'bg-violet-100 border border-violet-200' },
  ].filter(m => m.data !== null) : [];

  return (
    <>
      {/* Collapsed state - Fire icon button */}
      <AnimatePresence>
        {!isVisible && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => toggleVisibility(true)}
            style={{ right: 16 }}
            className="fixed bottom-4 z-50 p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200 group"
            title="Show Metal Prices"
          >
            <Flame className="h-5 w-5 text-white group-hover:animate-pulse" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Expanded state - Full bar */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{ left: leftOffset }}
            className={`fixed bottom-0 right-0 z-40 transition-all duration-300 ${className}`}
          >
            <div className={darkMode
              ? 'bg-[#0f1d33] border-t border-white/10 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.3)]'
              : 'bg-slate-100 border-t border-slate-200 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.06)]'
            }>
              <div className="max-w-screen-2xl mx-auto px-4 py-2">
                <div className="flex items-center justify-between gap-4">
                  {/* Left side - Title & Refresh */}
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      Live Spot
                    </span>
                    <button
                      onClick={fetchLivePrices}
                      disabled={loading}
                      className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`}
                      title="Refresh prices"
                    >
                      <RefreshCw className={`h-3 w-3 ${darkMode ? 'text-slate-400' : 'text-slate-600'} ${loading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>

                  {/* Center - Metal prices */}
                  <div className="flex items-center gap-3 sm:gap-6 overflow-x-auto scrollbar-hide">
                    {loading && metals.length === 0 ? (
                      <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loading prices...</span>
                    ) : metals.length === 0 ? (
                      <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Unable to fetch prices</span>
                    ) : (
                      metals.map((metal) => (
                        <div 
                          key={metal.name}
                          className="flex items-center gap-2 sm:gap-3 shrink-0"
                        >
                          {/* Metal symbol badge */}
                          <div className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            darkMode ? 'bg-white/10 border border-white/10 text-white/80' : `${metal.bg} ${metal.color}`
                          }`}>
                            {metal.symbol}
                          </div>
                          
                          {/* Prices */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                            <div className="flex items-center gap-1">
                              <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {formatPrice(metal.data!.price, metal.name === 'Silver' ? 2 : 0)}
                              </span>
                              <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>/oz</span>
                              {metal.data!.change !== 0 && (
                                <span className={`flex items-center text-[10px] font-medium ${metal.data!.change > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {metal.data!.change > 0 ? (
                                    <TrendingUp className="h-2.5 w-2.5" />
                                  ) : (
                                    <TrendingDown className="h-2.5 w-2.5" />
                                  )}
                                  <span className="ml-0.5">{metal.data!.change > 0 ? '+' : ''}{metal.data!.change.toFixed(2)}</span>
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className={`text-xs ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {formatPrice(metal.data!.price / OZ_TO_GRAM, 2)}
                              </span>
                              <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>/g</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Right side - Source & Close */}
                  <div className="flex items-center gap-2 shrink-0">
                    {lastFetch && (
                      <span className={`text-[9px] hidden md:inline ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                        {lastFetch.toLocaleTimeString()}
                      </span>
                    )}
                    <button
                      onClick={() => toggleVisibility(false)}
                      className={`p-1.5 rounded-lg transition-colors group ${darkMode ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`}
                      title="Hide Metal Prices"
                    >
                      <X className={`h-4 w-4 ${darkMode ? 'text-slate-500 group-hover:text-white' : 'text-slate-500 group-hover:text-slate-800'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MetalPricesBar;
